import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/ui/page-header';
import { useSuperuserGuard } from '@/hooks/use-superuser-guard';
import { EmailTemplateEditor } from '@/components/design/EmailTemplateEditor';
import { UrlTemplateEditor, NEW_URL_TEMPLATE, URL_TYPE_LABELS } from '@/components/design/UrlTemplateEditor';
import { emailTemplateService, type EmailTemplate } from '@/services/emailTemplateService';
import { urlTemplateService, type UrlTemplate } from '@/services/urlTemplateService';
import { useToast } from '@/hooks/use-toast';
import { Mail, Link, Plus, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { TEMPLATE_GROUPS, templateLabel } from '@/components/design/emailTemplateMeta';

const NEW_TEMPLATE = { name: '', template_type: 'user_invitation', subject_template: '', content_config: {}, color_config: {}, is_active: false } as unknown as EmailTemplate;
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { PAGE_HEADERS } from '@/constants/page-headers';

export default function Design() {
  const { isLoading: guardLoading, isSuperuser } = useSuperuserGuard();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Template editing state
  const [editingEmailTemplate, setEditingEmailTemplate] = useState<EmailTemplate | null>(null);
  const [editingUrlTemplate, setEditingUrlTemplate] = useState<UrlTemplate | null>(null);
  const [search, setSearch] = useState('');
  const [emailDirty, setEmailDirty] = useState(false);
  const [urlDirty, setUrlDirty] = useState(false);
  const [urlSearch, setUrlSearch] = useState('');
  const [activeTab, setActiveTab] = useState('email');
  const selectUrlTemplate = (template: UrlTemplate | null) => {
    if (urlDirty && !confirm('Discard your unsaved URL template changes?')) return;
    setUrlDirty(false);
    setEditingUrlTemplate(template);
  };
  const selectTemplate = (t: EmailTemplate) => {
    if (emailDirty && !confirm('Discard your unsaved changes?')) return;
    setEmailDirty(false);
    setEditingEmailTemplate(t);
  };

  // Fetch all email templates
  const { data: emailTemplates, isLoading: emailTemplatesLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => emailTemplateService.getAllTemplates(),
    enabled: isSuperuser
  });

  // Fetch all URL templates
  const { data: urlTemplates, isLoading: urlTemplatesLoading } = useQuery({
    queryKey: ['url-templates'],
    queryFn: () => urlTemplateService.getAllTemplates(),
    enabled: isSuperuser
  });

  // Email template mutations
  const saveEmailTemplateMutation = useMutation({
    mutationFn: (template: Omit<EmailTemplate, 'id' | 'created_at' | 'updated_at'>) => 
      emailTemplateService.saveTemplate(template),
    onSuccess: (saved: any) => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setEmailDirty(false);
      setEditingEmailTemplate(saved && saved.id ? (saved as EmailTemplate) : null);
      toast({ description: 'Email template saved successfully' });
    },
    onError: () => {
      toast({ description: 'Failed to save email template', variant: 'destructive' });
    }
  });

  const updateEmailTemplateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<EmailTemplate> }) =>
      emailTemplateService.updateTemplate(id, updates),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setEmailDirty(false);
      setEditingEmailTemplate((cur) => (cur && cur.id === vars.id ? ({ ...cur, ...vars.updates } as EmailTemplate) : cur));
      toast({ description: 'Email template updated successfully' });
    },
    onError: () => {
      toast({ description: 'Failed to update email template', variant: 'destructive' });
    }
  });

  const deleteEmailTemplateMutation = useMutation({
    mutationFn: (id: string) => emailTemplateService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setEditingEmailTemplate(null);
      toast({ description: 'Email template deleted successfully' });
    },
    onError: () => {
      toast({ description: 'Failed to delete email template', variant: 'destructive' });
    }
  });

  const setEmailTemplateActiveMutation = useMutation({
    mutationFn: ({ id, templateType }: { id: string; templateType: string }) =>
      emailTemplateService.setAsActive(id, templateType),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setEditingEmailTemplate((cur) => (cur && cur.id === vars.id ? ({ ...cur, is_active: true } as EmailTemplate) : cur));
      toast({ description: 'Email template is now active' });
    },
    onError: () => {
      toast({ description: 'Failed to activate email template', variant: 'destructive' });
    }
  });

  // URL template mutations
  const saveUrlTemplateMutation = useMutation({
    mutationFn: (template: Omit<UrlTemplate, 'id' | 'created_at' | 'updated_at'>) => 
      urlTemplateService.saveTemplate(template),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['url-templates'] });
      setEditingUrlTemplate(saved);
      setUrlDirty(false);
      toast({ description: 'URL template saved successfully' });
    },
    onError: () => {
      toast({ description: 'Failed to save URL template', variant: 'destructive' });
    }
  });

  const updateUrlTemplateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<UrlTemplate> }) =>
      urlTemplateService.updateTemplate(id, updates),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['url-templates'] });
      setEditingUrlTemplate(saved);
      setUrlDirty(false);
      toast({ description: 'URL template updated successfully' });
    },
    onError: () => {
      toast({ description: 'Failed to update URL template', variant: 'destructive' });
    }
  });

  const deleteUrlTemplateMutation = useMutation({
    mutationFn: (id: string) => urlTemplateService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['url-templates'] });
      setEditingUrlTemplate(null);
      setUrlDirty(false);
      toast({ description: 'URL template deleted successfully' });
    },
    onError: () => {
      toast({ description: 'Failed to delete URL template', variant: 'destructive' });
    }
  });

  const setUrlTemplateActiveMutation = useMutation({
    mutationFn: ({ id, templateType }: { id: string; templateType: string }) =>
      urlTemplateService.setAsActive(id, templateType),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['url-templates'] });
      setEditingUrlTemplate((current) => current?.id === vars.id ? { ...current, is_active: true } : current);
      toast({ description: 'URL template is now active' });
    },
    onError: () => {
      toast({ description: 'Failed to activate URL template', variant: 'destructive' });
    }
  });

  const groups = (() => {
    const q = search.trim().toLowerCase();
    const list = (emailTemplates || []).filter((t) => !q || [t.name, t.subject_template, templateLabel(t.template_type)].some((x) => (x || '').toLowerCase().includes(q)));
    const known = new Set(TEMPLATE_GROUPS.flatMap((g) => g.types));
    const sortFn = (a: EmailTemplate, b: EmailTemplate) =>
      a.template_type === b.template_type ? Number(b.is_active) - Number(a.is_active) : 0;
    const out = TEMPLATE_GROUPS.map((g) => ({
      label: g.label,
      items: list.filter((t) => g.types.includes(t.template_type)).sort((a, b) => g.types.indexOf(a.template_type) - g.types.indexOf(b.template_type) || sortFn(a, b)),
    }));
    out.push({ label: 'Other', items: list.filter((t) => !known.has(t.template_type)) });
    return out.filter((g) => g.items.length);
  })();
  const urlGroups = (Object.keys(URL_TYPE_LABELS) as UrlTemplate['template_type'][]).map((type) => ({
    type,
    templates: (urlTemplates || []).filter((t) => t.template_type === type && [t.name, t.url_template, URL_TYPE_LABELS[type]].some((s) => s?.toLowerCase().includes(urlSearch.trim().toLowerCase()))).sort((a, b) => Number(b.is_active) - Number(a.is_active)),
  })).filter((group) => group.templates.length);
  const otherUrlTemplates = (urlTemplates || []).filter((t) => !Object.prototype.hasOwnProperty.call(URL_TYPE_LABELS, t.template_type) && [t.name, t.url_template, t.template_type].some((s) => s?.toLowerCase().includes(urlSearch.trim().toLowerCase())));

  if (guardLoading || emailTemplatesLoading || urlTemplatesLoading) {
    return <div>Loading...</div>;
  }
  
  if (!isSuperuser) {
    return <div>Access denied. Superuser privileges required.</div>;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <main className="flex-1 p-6">
          <div className="space-y-6">
            <PageHeader {...PAGE_HEADERS.design} />
            
            <Tabs value={activeTab} onValueChange={(next) => {
              if (next === activeTab) return;
              if ((activeTab === 'url' && urlDirty || activeTab === 'email' && emailDirty) && !confirm('Discard your unsaved changes?')) return;
              if (activeTab === 'url') { setEditingUrlTemplate(null); setUrlDirty(false); }
              if (activeTab === 'email') { setEditingEmailTemplate(null); setEmailDirty(false); }
              setActiveTab(next);
            }} className="space-y-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Templates
                </TabsTrigger>
                <TabsTrigger value="url" className="flex items-center gap-2">
                  <Link className="h-4 w-4" />
                  URL Templates
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="email">
                <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-6">
                  <aside className={`space-y-3 ${editingEmailTemplate ? 'hidden lg:block' : ''}`}>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search emails" className="pl-8" />
                      </div>
                      <Button size="icon" onClick={() => selectTemplate(NEW_TEMPLATE)} aria-label="New email template"><Plus className="h-4 w-4" /></Button>
                    </div>
                    <nav className="rounded-lg border divide-y">
                      {groups.map((g) => (
                        <div key={g.label} className="py-2">
                          <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</div>
                          {g.items.map((t) => {
                            const selected = editingEmailTemplate?.id === t.id;
                            return (
                              <button
                                key={t.id}
                                type="button"
                                onClick={() => selectTemplate(t)}
                                className={`w-full text-left px-3 py-2 transition-colors ${selected ? 'bg-muted' : 'hover:bg-muted/60'} ${t.is_active ? '' : 'pl-6'}`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span className={`text-sm truncate ${t.is_active ? 'font-medium' : 'text-muted-foreground'}`}>{t.is_active ? templateLabel(t.template_type) : t.name}</span>
                                  {t.is_active ? <Badge className="text-[10px] px-1.5 py-0">Live</Badge> : <span className="text-[10px] text-muted-foreground">Draft</span>}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">{t.subject_template || 'No subject'}</div>
                              </button>
                            );
                          })}
                        </div>
                      ))}
                      {groups.length === 0 && <p className="p-4 text-sm text-muted-foreground">No emails match your search.</p>}
                    </nav>
                  </aside>

                  <section className={editingEmailTemplate ? '' : 'hidden lg:block'}>
                    {editingEmailTemplate ? (
                      <EmailTemplateEditor
                        key={editingEmailTemplate.id || 'new'}
                        template={editingEmailTemplate.id ? editingEmailTemplate : null}
                        onDirtyChange={setEmailDirty}
                        onSave={(template) => editingEmailTemplate.id
                          ? updateEmailTemplateMutation.mutate({ id: editingEmailTemplate.id!, updates: template })
                          : saveEmailTemplateMutation.mutate(template)}
                        onCancel={() => setEditingEmailTemplate(null)}
                        onDelete={editingEmailTemplate.id ? () => deleteEmailTemplateMutation.mutate(editingEmailTemplate.id!) : undefined}
                        onSetActive={editingEmailTemplate.id ? () => setEmailTemplateActiveMutation.mutate({ id: editingEmailTemplate.id!, templateType: editingEmailTemplate.template_type }) : undefined}
                        onDuplicate={(t) => {
                          const { id, created_at, updated_at, ...rest } = t as any;
                          saveEmailTemplateMutation.mutate({ ...rest, name: `${t.name} (copy)`, is_active: false });
                        }}
                      />
                    ) : (
                      <Card><CardContent className="p-10 text-center text-muted-foreground">
                        <Mail className="h-10 w-10 mx-auto mb-3" />
                        Choose an email on the left to edit its wording and see a live preview.
                      </CardContent></Card>
                    )}
                  </section>
                </div>
              </TabsContent>
              
              <TabsContent value="url">
                <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-6">
                  <aside className={`space-y-3 ${editingUrlTemplate ? 'hidden lg:block' : ''}`}>
                    <div className="flex gap-2">
                      <div className="relative flex-1"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input value={urlSearch} onChange={(e) => setUrlSearch(e.target.value)} placeholder="Search URLs" className="pl-8" /></div>
                      <Button size="icon" onClick={() => selectUrlTemplate({ ...NEW_URL_TEMPLATE })} aria-label="New URL template"><Plus className="h-4 w-4" /></Button>
                    </div>
                    <nav className="rounded-lg border divide-y">
                      {urlGroups.map((group) => <div key={group.type} className="py-2">
                        <div className="px-3 pb-1 text-xs font-semibold uppercase text-muted-foreground">{URL_TYPE_LABELS[group.type]}</div>
                        {group.templates.map((template) => <Button key={template.id} type="button" variant="ghost" onClick={() => selectUrlTemplate(template)} className={`w-full h-auto rounded-none flex flex-col items-stretch text-left px-3 py-2 ${editingUrlTemplate?.id === template.id ? 'bg-muted' : ''}`}>
                          <span className="flex items-center justify-between gap-2 min-w-0"><span className="text-sm truncate">{template.name}</span>{template.is_active ? <Badge className="text-[10px] px-1.5 py-0">Live</Badge> : <span className="text-[10px] text-muted-foreground">Draft</span>}</span>
                          <span className="text-xs text-muted-foreground font-mono truncate">{template.url_template || 'No URL pattern'}</span>
                        </Button>)}
                      </div>)}
                      {otherUrlTemplates.length > 0 && <div className="py-2"><div className="px-3 pb-1 text-xs font-semibold uppercase text-muted-foreground">Other</div>{otherUrlTemplates.map((template) => <Button key={template.id} variant="ghost" onClick={() => selectUrlTemplate(template)} className="w-full h-auto rounded-none flex flex-col items-stretch text-left px-3 py-2"><span className="flex justify-between gap-2"><span className="truncate text-sm">{template.name}</span>{template.is_active ? <Badge>Live</Badge> : <span className="text-xs text-muted-foreground">Draft</span>}</span><span className="text-xs text-muted-foreground truncate font-mono">{template.url_template}</span></Button>)}</div>}
                      {urlGroups.length === 0 && otherUrlTemplates.length === 0 && <p className="p-4 text-sm text-muted-foreground">{urlTemplates?.length ? 'No URLs match your search.' : 'No URL templates yet.'}</p>}
                    </nav>
                  </aside>
                  <section className={editingUrlTemplate ? 'min-w-0' : 'hidden lg:block'}>
                    {editingUrlTemplate ? <UrlTemplateEditor key={editingUrlTemplate.id || 'new'} template={editingUrlTemplate} onDirtyChange={setUrlDirty}
                      onSave={async (template) => editingUrlTemplate.id
                        ? updateUrlTemplateMutation.mutateAsync({ id: editingUrlTemplate.id, updates: template })
                        : saveUrlTemplateMutation.mutateAsync(template)}
                      onCancel={() => { setUrlDirty(false); setEditingUrlTemplate(null); }}
                      onDelete={editingUrlTemplate.id ? () => deleteUrlTemplateMutation.mutate(editingUrlTemplate.id) : undefined}
                      onSetActive={editingUrlTemplate.id ? () => setUrlTemplateActiveMutation.mutate({ id: editingUrlTemplate.id, templateType: editingUrlTemplate.template_type }) : undefined}
                    /> : <Card><CardContent className="p-10 text-center text-muted-foreground"><Link className="h-10 w-10 mx-auto mb-3" />Choose a URL template on the left to edit it and see a sample link.</CardContent></Card>}
                  </section>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}